/**
 * Asset picker modal: grid of tiles, single-select, save/clear/cancel.
 *
 * Intentionally decoupled from "folder thumbnail" internals — it takes the candidate `files`
 * + current override + save/clear callbacks from its consumer. That makes it reusable for
 * other pickers landing soon:
 *   -: project icon + project snapshot cover selection
 *   - (Future) any "pin an asset as a preview" flow
 *
 * When is implemented, hoist this component to `shared/ui/AssetPicker/`
 * And rename; right now it lives here to keep the diff contained.
 */
import { useState } from 'react';
import type { ProjectFileResponseDto } from '@inference.engine/workspace-api-client/web';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@monksflow/monks-ui';
import { AssetThumbnail } from '@/entities/project-file';
import type { ThumbnailPickerProps } from './ThumbnailPicker.types';
import { useTranslation } from 'react-i18next';

export function ThumbnailPicker(props: ThumbnailPickerProps) {
  const { t } = useTranslation('projects');

  const {
    files,
    currentOverrideFileId,
    isLoading,
    isSaving,
    onSave,
    onClear,
    open,
    onClose,
    title: titleProp,
    description: descriptionProp,
    emptyStateText: emptyStateTextProp,
  } = props;

  const title = titleProp ?? t('projectDetail.thumbnailPicker.changeFolderThumbnail');
  const description = descriptionProp ?? t('projectDetail.thumbnailPicker.pickAnyAssetInThis');
  const emptyStateText = emptyStateTextProp ?? t('projectDetail.thumbnailPicker.thisFolderHasNo');

  // Container only mounts this when isPickerOpen flips true, so useState's initial value
  // captures the current override on every fresh open. No useEffect needed for resync.
  const [selectedFileId, setSelectedFileId] = useState<string | null>(currentOverrideFileId);

  const hasOverride = currentOverrideFileId != null;
  const hasFiles = files.length > 0;
  const selectionChanged = selectedFileId !== currentOverrideFileId;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent
        scrollable
        size="lg"
        /**
         * Stop click propagation at the modal root — the picker is mounted as a child of a
         * clickable TableRow. Radix renders via portal but React synthetic events still
         * bubble through the React tree, so without this stopPropagation clicks inside the
         * modal (including the built-in close button) trigger the row's onClick handler.
         */
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('projectDetail.thumbnailPicker.loadingAssets')}
            </p>
          ) : !hasFiles ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{emptyStateText}</p>
          ) : (
            <ul
              role="radiogroup"
              aria-label={t('projectDetail.thumbnailPicker.assets')}
              className="grid grid-cols-2 gap-3 p-1 sm:grid-cols-3 md:grid-cols-4"
            >
              {files.map((file: ProjectFileResponseDto) => {
                const isSelected = selectedFileId === file.id;
                return (
                  <li key={file.id}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-label={t('projectDetail.thumbnailPicker.selectAsThumbnail', {
                        fileName: file.fileName,
                      })}
                      onClick={() => setSelectedFileId(file.id)}
                      className={`group flex w-full flex-col overflow-hidden rounded-lg border border-border text-left transition-[color,box-shadow] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                        isSelected
                          ? 'ring-2 ring-primary ring-offset-2'
                          : 'hover:border-foreground/40'
                      }`}
                    >
                      <div className="aspect-square w-full overflow-hidden bg-muted">
                        <AssetThumbnail file={file} className="size-full" iconSize="size-8" />
                      </div>
                      <div className="flex flex-col gap-0.5 px-3 py-2">
                        <span className="truncate text-xs font-medium text-foreground">
                          {file.fileName}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogBody>
        {/* `sm:` prefix required: DialogFooter ships `sm:justify-end`, and tailwind-merge keys by
            modifier — an unprefixed `justify-between` does not conflict with it and loses inside
            the media query, right-aligning the whole footer. */}
        <DialogFooter className="flex items-center gap-2 sm:justify-between">
          <Button
            type="button"
            variant="outline"
            size="default"
            disabled={!hasOverride || isSaving}
            onClick={onClear}
          >
            {t('projectDetail.thumbnailPicker.clearOverride')}
          </Button>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="default" onClick={onClose}>
              {t('projectDetail.thumbnailPicker.cancel')}
            </Button>
            <Button
              type="button"
              variant="default"
              size="default"
              disabled={isSaving || !hasFiles || !selectionChanged || selectedFileId == null}
              onClick={() => {
                if (selectedFileId != null) onSave(selectedFileId);
              }}
            >
              {isSaving
                ? t('projectDetail.thumbnailPicker.saving')
                : t('projectDetail.thumbnailPicker.save')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
